import {
  NOTIFICATION_EMAIL_NOTICE,
  TEAM_LEVELS,
  TEAM_LEVEL_LABELS,
  createTeamMemberSchema,
  type CreateTeamMemberInput,
  type TeamLevel,
  type TeamMember,
} from '@easyhelp/shared';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useSession } from '../../../api/auth';
import {
  useAddTeamMember,
  useRemoveTeamMember,
  useRevokeMemberSessions,
  useTeamMembers,
  useUpdateTeamMember,
} from '../../../api/team';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { MemberDialog } from './MemberDialog';
import { TeamGroupCard } from './TeamGroupCard';
import { workloadLabel } from './workload';

type DialogState =
  | { kind: 'closed' }
  | { kind: 'add'; level: TeamLevel }
  | { kind: 'edit'; member: TeamMember }
  | { kind: 'revoke'; member: TeamMember }
  | { kind: 'remove'; member: TeamMember };

/**
 * Impostazioni › Team (PRD-002 US-2, PAGE-003): chi può ricevere l'assegnazione di un ticket, su
 * due livelli. Aggiunta, modifica, spostamento di livello, chiusura delle sessioni e rimozione; la
 * rimozione è impedita a chi è responsabile di ticket aperti (DEC-004). Con l'MCP acceso ogni riga
 * apre anche le connessioni di Claude e Cursor del membro, con «Revoca» (PRD-007 US-2).
 */
export function TeamPage() {
  const { data, isPending, isError, error, refetch } = useTeamMembers();
  const addMember = useAddTeamMember();
  const updateMember = useUpdateTeamMember();
  // Istanza separata: l'errore di uno spostamento non deve comparire nel dialogo di modifica.
  const moveMember = useUpdateTeamMember();
  const removeMember = useRemoveTeamMember();
  const revokeSessions = useRevokeMemberSessions();
  const { data: session } = useSession();
  const [dialog, setDialog] = useState<DialogState>({ kind: 'closed' });
  const [notice, setNotice] = useState<string | null>(null);
  // Connessioni di Claude e Cursor per membro (#152), solo con l'MCP acceso sull'istanza. `?.` anche su
  // `features`: un'API precedente a #152 (rilascio in corso) non lo manda, e vale come MCP spento.
  const mcp = session?.features?.mcp ? { onRevoked: setNotice } : undefined;

  const close = () => {
    setDialog({ kind: 'closed' });
    addMember.reset();
    updateMember.reset();
    removeMember.reset();
    revokeSessions.reset();
  };

  const submitMember = (input: CreateTeamMemberInput) => {
    // Lo stesso schema dell'API: email minuscole, notifica vuota = nessuna.
    const values = createTeamMemberSchema.parse(input);
    if (dialog.kind === 'add') {
      addMember.mutate(values, {
        onSuccess: (member) => {
          close();
          setNotice(`${member.name} è nel team: può entrare nella suite e ricevere ticket.`);
        },
      });
    } else if (dialog.kind === 'edit') {
      const { email: _email, ...changes } = values;
      updateMember.mutate(
        { id: dialog.member.id, changes },
        {
          onSuccess: (member) => {
            close();
            setNotice(`Dati di ${member.name} aggiornati.`);
          },
        }
      );
    }
  };

  const moveLevel = (member: TeamMember) => {
    const level: TeamLevel = member.level === 'primo' ? 'secondo' : 'primo';
    moveMember.mutate(
      { id: member.id, changes: { level } },
      {
        onSuccess: () =>
          setNotice(`${member.name} è passato al ${TEAM_LEVEL_LABELS[level]}. I ticket già assegnati restano suoi.`),
        onError: (e) => setNotice(`Spostamento non riuscito: ${e.message}`),
      }
    );
  };

  const confirmRevoke = () => {
    if (dialog.kind !== 'revoke') return;
    const { member } = dialog;
    revokeSessions.mutate(member.id, {
      onSuccess: () => {
        close();
        setNotice(`Sessioni di ${member.name} chiuse: dovrà accedere di nuovo.`);
      },
    });
  };

  const confirmRemove = () => {
    if (dialog.kind !== 'remove') return;
    const { member } = dialog;
    removeMember.mutate(member.id, {
      onSuccess: () => {
        close();
        setNotice(`${member.name} non è più nel team: le sue sessioni sono chiuse.`);
      },
    });
  };

  return (
    <Stack spacing={4}>
      <Stack spacing={1}>
        <Typography variant="h5" component="h2">
          Team di supporto
        </Typography>
        <Typography color="text.secondary">
          Chi può ricevere l'assegnazione di un ticket. {NOTIFICATION_EMAIL_NOTICE}
        </Typography>
      </Stack>

      {isPending && (
        <Stack direction="row" spacing={2} alignItems="center" role="status">
          <CircularProgress size={20} />
          <Typography>Caricamento del team…</Typography>
        </Stack>
      )}

      {isError && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void refetch()}>
              Riprova
            </Button>
          }
        >
          <AlertTitle>Team non disponibile</AlertTitle>
          {error.message}
        </Alert>
      )}

      {data &&
        TEAM_LEVELS.map((level) => (
          <TeamGroupCard
            key={level}
            level={level}
            members={data.members.filter((member) => member.level === level)}
            onAdd={(l) => setDialog({ kind: 'add', level: l })}
            onEdit={(member) => setDialog({ kind: 'edit', member })}
            onMove={moveLevel}
            onRevokeSessions={(member) => setDialog({ kind: 'revoke', member })}
            onRemove={(member) => setDialog({ kind: 'remove', member })}
            mcp={mcp}
          />
        ))}

      {(dialog.kind === 'add' || dialog.kind === 'edit') && (
        <MemberDialog
          open
          key={dialog.kind === 'edit' ? dialog.member.id : `add-${dialog.level}`}
          level={dialog.kind === 'add' ? dialog.level : dialog.member.level}
          member={dialog.kind === 'edit' ? dialog.member : undefined}
          error={dialog.kind === 'add' ? addMember.error?.message : updateMember.error?.message}
          busy={addMember.isPending || updateMember.isPending}
          onSubmit={submitMember}
          onClose={close}
        />
      )}

      {dialog.kind === 'revoke' && (
        <ConfirmDialog
          open
          title={`Chiudere le sessioni di ${dialog.member.name}?`}
          confirmLabel="Chiudi le sessioni"
          error={revokeSessions.error?.message}
          busy={revokeSessions.isPending}
          onConfirm={confirmRevoke}
          onClose={close}
        >
          {dialog.member.name} resta nel team ma dovrà accedere di nuovo con Google su ogni dispositivo e ricollegare
          Claude o Cursor.
          {session?.member.id === dialog.member.id && ' Sono le tue sessioni: uscirai anche tu da questa.'}
        </ConfirmDialog>
      )}

      {dialog.kind === 'remove' && (
        <ConfirmDialog
          open
          title={`Rimuovere ${dialog.member.name} dal team?`}
          confirmLabel="Rimuovi"
          confirmColor="error"
          error={removeMember.error?.message}
          blocked={
            dialog.member.openTicketCount > 0 ||
            removeMember.error?.code === 'team.member_has_open_tickets' ||
            removeMember.error?.code === 'team.last_admin'
          }
          busy={removeMember.isPending}
          onConfirm={confirmRemove}
          onClose={close}
        >
          {dialog.member.openTicketCount > 0 ? (
            <>
              {dialog.member.name} è responsabile di {workloadLabel(dialog.member.openTicketCount).toLowerCase()}:
              cambia il responsabile di quei ticket prima di rimuoverlo. Essere collaboratore non blocca la rimozione.
            </>
          ) : (
            <>
              Non potrà più entrare nella suite né ricevere ticket: le sue sessioni e i collegamenti di Claude e Cursor
              vengono chiusi subito. I ticket già chiusi restano nello storico.
            </>
          )}
        </ConfirmDialog>
      )}

      <Snackbar open={Boolean(notice)} autoHideDuration={6000} onClose={() => setNotice(null)} message={notice} />
    </Stack>
  );
}
